import { Empty, Flex, Spin } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";
import {
  usePublicThemes,
  useOrgThemes,
  type ThemeResponse,
} from "../../../themeEditor/hooks/useThemeApi";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useApplyTheme } from "../../hooks/useApplyTheme";
import { ThemePreviewModal } from "./components/ThemePreviewModal";
import { ThemeDropdown } from "./components/ThemeDropdown";
import { aspectRatioNumberToEnum } from "../../../themeEditor/helpers";

export type ThemeGroup = {
  key: string;
  label: string;
  themes: ThemeResponse[];
};

export const ThemeSelector = () => {
  const t = useTranslate();

  const {
    templatesData: { organizationId },
    mediaGenerationSettingsData: { mediaAspectRatio },
  } = useTemplateData();

  const { data: publicThemesData, isLoading: isPublicLoading } =
    usePublicThemes();
  const { data: orgThemesData, isLoading: isOrgLoading } =
    useOrgThemes(organizationId);

  const projectAspectRatio = useMemo(
    () =>
      mediaAspectRatio ? aspectRatioNumberToEnum(mediaAspectRatio) : undefined,
    [mediaAspectRatio]
  );

  const publicThemes = useMemo(() => {
    const items = publicThemesData?.items ?? [];
    return projectAspectRatio
      ? items.filter((theme) => theme.aspect_ratio === projectAspectRatio)
      : items;
  }, [publicThemesData, projectAspectRatio]);

  const orgThemes = useMemo(() => {
    const items = orgThemesData?.items ?? [];
    return projectAspectRatio
      ? items.filter((theme) => theme.aspect_ratio === projectAspectRatio)
      : items;
  }, [orgThemesData, projectAspectRatio]);

  const { applyTheme } = useApplyTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalTheme, setModalTheme] = useState<ThemeResponse | null>(null);

  const themeGroups = useMemo(() => {
    const groups: ThemeGroup[] = [];
    if (publicThemes.length > 0) {
      groups.push({
        key: "public-group",
        label: t("media.themeEditor.publicThemes"),
        themes: publicThemes,
      });
    }
    if (orgThemes.length > 0) {
      groups.push({
        key: "org-group",
        label: t("media.themeEditor.myThemes"),
        themes: orgThemes,
      });
    }
    return groups;
  }, [publicThemes, orgThemes, t]);

  const handleSelectTheme = useCallback((themeItem: ThemeResponse) => {
    setDropdownOpen(false);
    setModalTheme(themeItem);
  }, []);

  const handleApply = useCallback(() => {
    if (modalTheme) {
      applyTheme(modalTheme);
      setModalTheme(null);
    }
  }, [modalTheme, applyTheme]);

  if (isPublicLoading || isOrgLoading) {
    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Spin size="small" />
      </Flex>
    );
  }

  if (themeGroups.length === 0) {
    return (
      <Empty
        description={t("media.themeEditor.noThemes")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <ThemeDropdown
        themeGroups={themeGroups}
        handleSelectTheme={handleSelectTheme}
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
      />
      <ThemePreviewModal
        selectedTheme={modalTheme}
        themeGroups={themeGroups}
        onSelectTheme={setModalTheme}
        onApply={handleApply}
        onClose={() => setModalTheme(null)}
      />
    </>
  );
};
